import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import confetti from 'canvas-confetti';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

interface DebatCategory {
  name: string;
  color: string;
  icon: string;
  questions: string[];
}

interface DebatWheelProps {
  players: string[];
  categories: DebatCategory[];
}

const SLOT_DURATION = 1200;
const SLOT_TICKS = 16;
const SVG_SIZE = 300;
const CENTER = SVG_SIZE / 2;
const RADIUS = 135;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

export default function DebatWheel({ players, categories }: DebatWheelProps) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotationDeg, setRotationDeg] = useState(0);
  const [winningCategory, setWinningCategory] = useState<DebatCategory | null>(null);
  const [resultQuestion, setResultQuestion] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [showNewDebatBtn, setShowNewDebatBtn] = useState(false);
  const animationRef = useRef<number>(0);
  const lastSegmentRef = useRef(-1);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastQuestionRef = useRef<string>('');

  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [isSlotSpinning, setIsSlotSpinning] = useState(true);
  const [slotDisplayName, setSlotDisplayName] = useState('');
  const slotTimers = useRef<number[]>([]);
  const turnCount = useRef(0);

  const shuffledOrder = useMemo(() => {
    const indices = players.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, [players]);

  const currentPlayer = players[shuffledOrder[currentPlayerIndex % shuffledOrder.length]] || '';

  const runSlotMachine = useCallback((targetName: string) => {
    setIsSlotSpinning(true);
    setWinningCategory(null);
    setResultQuestion(null);
    setShowResult(false);
    setShowNewDebatBtn(false);

    const interval = SLOT_DURATION / SLOT_TICKS;
    for (let i = 0; i < SLOT_TICKS; i++) {
      const timerId = window.setTimeout(() => {
        if (i < SLOT_TICKS - 1) {
          setSlotDisplayName(players[Math.floor(Math.random() * players.length)]);
        } else {
          setSlotDisplayName(targetName);
          setIsSlotSpinning(false);
          try { Haptics.impact({ style: ImpactStyle.Medium }); } catch {}
        }
      }, interval * i + (i > SLOT_TICKS * 0.7 ? (i - SLOT_TICKS * 0.7) * 30 : 0));
      slotTimers.current.push(timerId);
    }
  }, [players]);

  useEffect(() => {
    const timers = slotTimers.current;
    runSlotMachine(currentPlayer);
    return () => { timers.forEach(id => window.clearTimeout(id)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segmentAngle = 360 / categories.length;

  const segments = useMemo(() => categories.map((cat, i) => {
    const startAngle = i * segmentAngle;
    const endAngle = startAngle + segmentAngle;
    const midAngle = startAngle + segmentAngle / 2;
    const textR = RADIUS * 0.58;
    const pos = polarToCartesian(CENTER, CENTER, textR, midAngle);

    return {
      ...cat,
      path: describeArc(CENTER, CENTER, RADIUS, startAngle, endAngle),
      textX: pos.x,
      textY: pos.y,
      midAngle,
    };
  }), [categories, segmentAngle]);

  // Update SVG rotation directly on DOM — no React re-render per frame
  const updateRotation = useCallback((deg: number) => {
    const svg = svgRef.current;
    if (svg) {
      const g = svg.getElementById('wheel-group');
      if (g) g.setAttribute('transform', `rotate(${deg} ${CENTER} ${CENTER})`);
    }
  }, []);

  const spin = useCallback(() => {
    if (isSpinning || isSlotSpinning) return;
    setIsSpinning(true);
    setWinningCategory(null);
    setResultQuestion(null);
    setShowResult(false);
    setShowNewDebatBtn(false);
    lastSegmentRef.current = -1;

    const extraDeg = 360 * (6 + Math.random() * 6) + Math.random() * 360;
    const totalRotation = rotationDeg + extraDeg;
    const duration = 3000 + Math.random() * 2000;
    const startTime = performance.now();
    const startRot = rotationDeg;

    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const animate = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const currentDeg = startRot + (totalRotation - startRot) * easeOut(progress);

      // Direct DOM update — no setState per frame
      updateRotation(currentDeg);

      const normDeg = ((currentDeg % 360) + 360) % 360;
      const currentSeg = Math.floor(normDeg / segmentAngle);
      if (currentSeg !== lastSegmentRef.current && lastSegmentRef.current !== -1) {
        try { Haptics.impact({ style: ImpactStyle.Light }); } catch {}
      }
      lastSegmentRef.current = currentSeg;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Store final rotation in state for persistence
        setRotationDeg(currentDeg);
        setIsSpinning(false);

        const finalNorm = ((currentDeg % 360) + 360) % 360;
        const indicatorDeg = (360 - finalNorm % 360) % 360;
        const winIdx = Math.floor(indicatorDeg / segmentAngle) % categories.length;
        const winner = categories[winIdx];

        setWinningCategory(winner);

        let q = 'Question à venir... 🔥';
        if (winner.questions.length > 0) {
          const available = winner.questions.filter(x => x !== lastQuestionRef.current);
          const pool = available.length > 0 ? available : winner.questions;
          q = pool[Math.floor(Math.random() * pool.length)];
          lastQuestionRef.current = q;
        }
        setResultQuestion(q);

        try { Haptics.impact({ style: ImpactStyle.Heavy }); } catch {}

        confetti({
          particleCount: 60,
          spread: 60,
          origin: { y: 0.5 },
          colors: ['#4ADE80', '#10B981', '#6EE7B7', '#166534'],
        });

        setTimeout(() => setShowResult(true), 400);
        setTimeout(() => setShowNewDebatBtn(true), 1400);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [isSpinning, isSlotSpinning, rotationDeg, segmentAngle, categories, updateRotation]);

  useEffect(() => {
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, []);

  const handleNewDebat = () => {
    setShowResult(false);
    setShowNewDebatBtn(false);
    setWinningCategory(null);
    setResultQuestion(null);
    turnCount.current += 1;
    const nextIndex = (currentPlayerIndex + 1) % players.length;
    setCurrentPlayerIndex(nextIndex);
    const nextPlayer = players[shuffledOrder[nextIndex % shuffledOrder.length]] || '';
    runSlotMachine(nextPlayer);
  };

  const canSpin = !isSpinning && !isSlotSpinning;
  const showSpinButton = canSpin && !winningCategory;

  return (
    <div className="h-screen bg-gradient-to-b from-[#052e16] via-[#0a3d1e] to-[#021a0b] flex flex-col items-center safe-top safe-bottom relative overflow-hidden">
      <DebatParticles />

      <div className="absolute right-6 z-10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>
        <span className="text-white/30 font-body text-sm">Tour {turnCount.current + 1}</span>
      </div>

      {/* Player name */}
      <div className="flex-shrink-0 pt-14 pb-2 px-6 text-center z-10">
        <p className="text-green-400/50 font-body text-xs mb-1">C'est au tour de...</p>
        <div className="h-12 flex items-center justify-center overflow-hidden">
          <h1
            key={slotDisplayName}
            className={`font-display font-black text-3xl text-white drop-shadow-lg ${
              isSlotSpinning ? 'slot-tick' : 'slot-land'
            }`}
          >
            {slotDisplayName}
          </h1>
        </div>
      </div>

      {/* Wheel + button area — fixed layout, never shifts */}
      {!showResult && (
        <div className="flex-1 flex flex-col items-center justify-center z-10">
          {/* Indicator — fixed position */}
          <div className="mb-[-10px] z-20 flex-shrink-0">
            <div
              className="debat-indicator"
              style={{
                width: 0, height: 0,
                borderLeft: '12px solid transparent',
                borderRight: '12px solid transparent',
                borderTop: '22px solid #FFD700',
                filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.5))',
              }}
            />
          </div>

          {/* SVG Wheel — fixed size container, rotation via SVG transform */}
          <div
            className={`flex-shrink-0 ${canSpin ? 'cursor-pointer' : ''}`}
            onClick={canSpin ? spin : undefined}
            style={{ width: '70vw', maxWidth: '340px', aspectRatio: '1' }}
          >
            <svg
              ref={svgRef}
              viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
              width="100%"
              height="100%"
            >
              {/* Outer glow ring — not inside rotating group */}
              <circle cx={CENTER} cy={CENTER} r={RADIUS + 4} fill="none" stroke="rgba(74,222,128,0.3)" strokeWidth="3" />

              {/* Rotating group — segments + labels all rotate together */}
              <g id="wheel-group" transform={`rotate(${rotationDeg} ${CENTER} ${CENTER})`}>
                {segments.map((seg, i) => {
                  const r = RADIUS * 0.58;
                  const rad = (seg.midAngle - 90) * Math.PI / 180;
                  const x = CENTER + r * Math.cos(rad);
                  const y = CENTER + r * Math.sin(rad);
                  const lines = seg.name.split('/');
                  return (
                    <g key={i}>
                      <path d={seg.path} fill={seg.color} stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
                      <text x={x} y={y - 14} textAnchor="middle" dominantBaseline="central" fontSize="22">{seg.icon}</text>
                      <text x={x} y={y + 6} textAnchor="middle" dominantBaseline="central" fill="white" fontSize="10" fontWeight="bold" fontFamily="Poppins, sans-serif">{lines[0]}</text>
                      {lines[1] && (
                        <text x={x} y={y + 19} textAnchor="middle" dominantBaseline="central" fill="white" fontSize="10" fontWeight="bold" fontFamily="Poppins, sans-serif">{lines[1]}</text>
                      )}
                    </g>
                  );
                })}
              </g>

              {/* Center circle — static, above the rotating group */}
              <circle cx={CENTER} cy={CENTER} r={22} fill="#052e16" stroke="rgba(74,222,128,0.4)" strokeWidth="2.5" />
              <text x={CENTER} y={CENTER + 1} textAnchor="middle" dominantBaseline="central" fontSize="18">🔥</text>
            </svg>
          </div>

          {/* Spin button — always takes up space, visibility toggles */}
          <div className="flex-shrink-0 mt-6" style={{ height: '56px' }}>
            {showSpinButton && (
              <button
                onClick={spin}
                className="px-10 py-4 rounded-2xl bg-gradient-to-r from-green-400 to-emerald-600 text-white font-display font-bold text-base btn-press shadow-[0_4px_20px_rgba(74,222,128,0.4)] animate-pulse-glow"
              >
                Tourner la roue 🎡
              </button>
            )}
          </div>
        </div>
      )}

      {/* Result */}
      {showResult && winningCategory && resultQuestion && (
        <ResultCard
          category={winningCategory}
          question={resultQuestion}
          showButton={showNewDebatBtn}
          onNewDebat={handleNewDebat}
        />
      )}
    </div>
  );
}

function getDynamicFontSize(text: string): string {
  const len = text.length;
  if (len < 50) return '22px';
  if (len < 80) return '20px';
  if (len < 110) return '18px';
  return '16px';
}

function darkenColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const f = 0.55;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

function ResultCard({ category, question, showButton, onNewDebat }: {
  category: DebatCategory;
  question: string;
  showButton: boolean;
  onNewDebat: () => void;
}) {
  const fontSize = getDynamicFontSize(question);
  const cardBgEnd = darkenColor(category.color);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 z-10 animate-fade-in">
      {/* Category reveal */}
      <div className="mb-5 text-center debat-zoom-in">
        <span className="text-5xl mb-2 block">{category.icon}</span>
        <h2
          className="font-display font-black text-2xl drop-shadow-lg"
          style={{ color: category.color }}
        >
          {category.name}
        </h2>
      </div>

      {/* Question card — styled per category */}
      <div className="w-full max-w-sm debat-card-spring">
        <div
          className="rounded-3xl p-6 border"
          style={{
            background: `linear-gradient(135deg, ${category.color} 0%, ${cardBgEnd} 100%)`,
            borderColor: `${category.color}55`,
            boxShadow: `0 0 30px ${category.color}33, 0 8px 24px ${category.color}22`,
          }}
        >
          {/* Icon */}
          <div className="text-center mb-3">
            <span className="text-3xl">{category.icon}</span>
          </div>

          {/* Question text — dynamic font size */}
          <p
            className="text-white font-display font-bold text-center leading-relaxed mb-5 flex items-center justify-center"
            style={{
              fontSize,
              minHeight: '100px',
              padding: '0 4px',
            }}
          >
            {question}
          </p>

          {/* Débattez */}
          <p
            className="font-display font-bold text-sm text-center debat-pulse"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            Débattez ! 🗣️
          </p>
        </div>
      </div>

      {/* New debate button */}
      {showButton && (
        <button
          onClick={onNewDebat}
          className="mt-6 w-full max-w-sm py-4 rounded-2xl bg-gradient-to-r from-green-400 to-emerald-600 text-white font-display font-bold text-base btn-press animate-fade-in shadow-[0_4px_20px_rgba(74,222,128,0.3)]"
        >
          Nouveau débat →
        </button>
      )}
    </div>
  );
}

function DebatParticles() {
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 3 + 1.5,
    duration: Math.random() * 6 + 4,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full bg-green-400/10"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animation: `float ${p.duration}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}
